import type { LegacyGate, TeamPermission } from './team.permissions.utils';

export type RoutePermission = {
  /**
   * Every permission the surface needs before it can load, not just the one
   * naming it. A view that resolves a second resource, or reads `GET /team`
   * for its own form state, needs those listed here too — otherwise the gate
   * lets a member reach a page whose own queries are forbidden.
   */
  permissions: readonly TeamPermission[];
  legacy: LegacyGate;
};

/**
 * Permission required to reach a top-level product area.
 *
 * Navigation items and route elements read the same entry so a visible link
 * can never point at a route that redirects away. Each entry is the set of
 * reads the area's own queries perform: `/owners` lists contacts but resolves
 * listing names for active ownerships, so it needs both.
 *
 * `legacy` records what the surface did with legacy roles. Product
 * areas were reachable by every member, so they all fall back to `allow` and
 * adding a gate cannot change behavior for an unmigrated team.
 *
 * The dashboard at `/` is deliberately absent. It stays ungated so it is
 * always a valid redirect target for a denied route, and its widgets gate
 * themselves individually instead.
 */
export const ROUTE_PERMISSIONS = {
  reconciliation: { permissions: ['bank-records:read'], legacy: 'allow' },
  // Deposits and expenses are both transaction surfaces and share one entry.
  transactions: { permissions: ['transactions:read'], legacy: 'allow' },
  reservations: { permissions: ['reservations:read'], legacy: 'allow' },
  listings: { permissions: ['listings:read'], legacy: 'allow' },
  // The owners table resolves listing names for every active ownership.
  owners: { permissions: ['contacts:read', 'listings:read'], legacy: 'allow' },
  contacts: { permissions: ['contacts:read'], legacy: 'allow' },
  feesCommissions: { permissions: ['recurring-fees:read'], legacy: 'allow' },
  reports: { permissions: ['reports:read'], legacy: 'allow' },
  statements: { permissions: ['statements:read'], legacy: 'allow' },
  // A single statement resolves its ownership periods and reads `GET /team`
  // for the statement header, beyond the list's own read.
  statementDetail: {
    permissions: ['statements:read', 'ownerships:read', 'teams:read'],
    legacy: 'allow',
  },
  // The layouts view reads `GET /team` for its statement header preview.
  statementLayouts: {
    permissions: ['statement-layouts:read', 'teams:read'],
    legacy: 'allow',
  },
  connections: { permissions: ['integrations:read'], legacy: 'allow' },
  files: { permissions: ['files:read'], legacy: 'allow' },
  generalLedger: { permissions: ['general-ledger:read'], legacy: 'allow' },
  accounts: { permissions: ['accounts:read'], legacy: 'allow' },
} as const satisfies Record<string, RoutePermission>;

export type RoutePermissionKey = keyof typeof ROUTE_PERMISSIONS;

/**
 * Permission required to reach a settings page.
 *
 * Unlike product areas these were not uniformly reachable before the feature,
 * so `legacy` differs per entry and is taken from the code each gate replaces.
 *
 * Several pages read `GET /team` through `useTeamQuery` for their form state,
 * so they require `teams:read` alongside the permission that names them.
 *
 * Two settings surfaces are intentionally absent from this read-only map:
 *
 * - Billing keeps its existing legacy team-admin gate. There is no billing
 *   permission in the API catalog.
 * - ACH payments keeps `canAccessSettingsAchPayments`, which combines its two
 *   required primitives with partner-managed, direct-member, and feature state
 *   that this map cannot express.
 */
export const SETTINGS_PERMISSIONS = {
  teamGeneral: {
    permissions: ['teams:read'],
    legacy: 'allow',
  },
  teamSecurity: { permissions: ['team-settings:write'], legacy: 'teamAdmin' },
  teamMembers: { permissions: ['team-members:read'], legacy: 'allow' },
  // The entry and route were unconditional before the feature, so the legacy
  // gate must keep every member's access on an unmigrated team.
  accountingBooksClosed: { permissions: ['books:close'], legacy: 'allow' },
  accountingAccounts: { permissions: ['accounts:read'], legacy: 'allow' },
  accountingPreferences: {
    permissions: ['team-settings:read'],
    legacy: 'allow',
  },
  accountingTaxRates: { permissions: ['tax-rates:read'], legacy: 'allow' },
  accountingOpeningBalances: {
    permissions: ['general-ledger:read', 'teams:read'],
    legacy: 'allow',
  },
  featuresVrpilotGuide: {
    permissions: ['team-settings:read'],
    legacy: 'allow',
  },
  featuresOwnerPortal: {
    permissions: ['team-settings:read', 'teams:read'],
    legacy: 'allow',
  },
  featuresEmails: { permissions: ['team-settings:read'], legacy: 'allow' },
  featuresOwnerStatements: {
    permissions: ['statement-layouts:read', 'teams:read'],
    legacy: 'allow',
  },
  integrationsApiTokens: {
    permissions: ['api-tokens:manage'],
    legacy: 'teamAdmin',
  },
} as const satisfies Record<string, RoutePermission>;

export type SettingsPermissionKey = keyof typeof SETTINGS_PERMISSIONS;

/**
 * Where a denied product route sends the user. The dashboard is never
 * permission-gated, so it is always reachable.
 */
export const DENIED_ROUTE_FALLBACK = '/';

/**
 * Where a denied settings page sends the user. Profile requires `self:read`,
 * which every bundle grants, so it is reachable by any member and keeps the
 * user inside settings instead of bouncing them to the dashboard.
 */
export const SETTINGS_FALLBACK_ROUTE = '/settings/profile';
